import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Spinner } from '@/components/ui/EmptyState';
import { Seo } from '@/components/Seo';
import { supabase } from '@/lib/supabaseClient';
import type { Article } from '@/types/article';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import ContentV2 from './v2/ContentV2';

export default function ContentHubPage() {
  const { isNew } = useSiteDesign();
  return isNew ? <ContentV2 /> : <ClassicContentHub />;
}

function ClassicContentHub() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('All');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const categories = ['All', 'Skincare Tips', 'Before & After', 'Events', 'Brand News'];

  useEffect(() => {
    supabase
      .from('rxk_articles')
      .select('id, slug, category, category_color, read_time, title, excerpt, author, author_role, published_date, image, body, status')
      .eq('status', 'published')
      .order('published_date', { ascending: false })
      .then(({ data }) => {
        setArticles((data as Article[]) ?? []);
        setLoading(false);
      });
  }, []);

  const filtered =
    activeCategory === 'All'
      ? articles
      : articles.filter((a) => a.category === activeCategory);

  return (
    <>
      <Seo pageKey="content" />
      <main className="w-full min-h-screen bg-white font-sans">
        <SharedNavbar />

        {/* ===== HERO ===== */}
        <section className="w-full bg-[#1A1A1A] pt-28 pb-16 md:pt-32 md:pb-20 px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-6 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('content.hero.label')}
              </span>
              <div className="w-6 h-px bg-[#7B1113]" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] mb-4">
              {t('content.hero.heading')}
            </h1>
            <p className="text-sm text-white/40 font-light max-w-lg mx-auto">
              {t('content.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* ===== FILTER TABS ===== */}
        <section className="w-full bg-white py-8 px-6 md:px-12 lg:px-20 border-b border-[#E8E8E6]">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 text-[11px] tracking-[0.12em] uppercase rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#7B1113] text-white'
                    : 'bg-[#F0EFED] text-[#8A8A8A] hover:text-[#2A2A2A]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* ===== ARTICLE GRID ===== */}
        <section className="w-full bg-white py-12 md:py-16 px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="flex justify-center py-20">
                <Spinner />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-sm text-[#8A8A8A] font-light">
                  No articles in this category yet.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {filtered.map((article) => (
                  <Link
                    key={article.id}
                    to={`/content/${article.slug}`}
                    className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-[#E8E8E6] hover:shadow-lg hover:border-transparent transition-all cursor-pointer"
                  >
                    {/* Image */}
                    <div className="relative w-full h-52 overflow-hidden">
                      <img
                        src={article.image ?? ''}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className="text-[9px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white font-medium"
                          style={{ backgroundColor: article.category_color }}
                        >
                          {article.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col gap-3 p-6">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#8A8A8A] font-light">
                          {article.read_time}
                        </span>
                      </div>

                      <h3 className="font-serif text-xl font-semibold text-[#2A2A2A] leading-tight group-hover:text-[#7B1113] transition-colors">
                        {article.title}
                      </h3>

                      <p className="text-sm text-[#8A8A8A] font-light leading-[1.7] line-clamp-2">
                        {article.excerpt}
                      </p>

                      <div className="flex items-center gap-2 mt-1 pt-3 border-t border-[#F0EFED]">
                        <span className="text-[10px] text-[#2A2A2A] font-medium">
                          {article.author}
                        </span>
                        <span className="text-[#8A8A8A]/40">&middot;</span>
                        <span className="text-[10px] text-[#8A8A8A] font-light">
                          {article.published_date}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <SharedFooter />
      </main>
    </>
  );
}